<template>
  <div class="relative">
    <button
      @click="toggleNotifications"
      class="p-2 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100/80 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 transition-all duration-200 relative"
    >
      <i class="fas fa-bell h-5 w-5" aria-hidden="true"></i>
      <ClientOnly>
        <span
          v-if="adminStore.unreadCount > 0"
          class="notification-badge absolute -top-1 -right-1 h-3 w-3 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold"
        >
          {{ adminStore.unreadCount > 9 ? '9+' : adminStore.unreadCount }}
        </span>
      </ClientOnly>
    </button>

    <!-- Notification Dropdown -->
    <div
      v-if="showNotifications"
      class="notification-dropdown absolute right-0 mt-2 w-[22rem] sm:w-96 bg-white/95 backdrop-blur-lg rounded-2xl shadow-lg border border-gray-100 z-50 overflow-hidden flex flex-col origin-top-right transition-all"
    >
      <!-- Header -->
      <div class="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
        <h3 class="text-sm font-semibold text-gray-900">Notifications</h3>
        <div class="flex items-center space-x-2">
          <button
            v-if="adminStore.unreadCount > 0"
            @click="markAllAsRead"
            class="text-xs text-blue-600 hover:text-blue-800 font-medium"
            :disabled="loading"
          >
            Mark all read
          </button>
          <button
            @click="refreshNotifications"
            class="p-1 rounded text-gray-400 hover:text-gray-600"
            :disabled="loading"
          >
            <i class="fas fa-refresh h-3 w-3" :class="{ 'animate-spin': loading }"></i>
          </button>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading && notifications.length === 0" class="p-4 text-center">
        <i class="fas fa-spinner fa-spin text-gray-400 mb-2"></i>
        <p class="text-sm text-gray-500">Loading notifications...</p>
      </div>

      <!-- Empty State -->
      <div v-else-if="notifications.length === 0" class="p-4 text-center">
        <i class="fas fa-bell-slash text-gray-300 mb-2"></i>
        <p class="text-sm text-gray-500">No notifications</p>
      </div>

      <!-- Notifications List -->
      <div v-else class="max-h-[26rem] overflow-y-auto overflow-x-hidden custom-scrollbar divide-y divide-gray-50/50">
        <div
          v-for="notification in notifications"
          :key="notification.id"
          class="px-4 py-3.5 hover:bg-gray-50/80 transition-colors duration-200 cursor-pointer relative"
          :class="{ 'bg-blue-50/20': !notification.is_read }"
          @click="navigateToContact(notification)"
        >
          <div class="flex items-start space-x-3">
            <!-- Icon -->
            <div class="flex-shrink-0">
              <div
                class="h-8 w-8 rounded-full flex items-center justify-center"
                :class="getNotificationIconClass(notification.type)"
              >
                <i :class="getNotificationIcon(notification.type)" class="h-4 w-4"></i>
              </div>
            </div>

            <!-- Content -->
            <div class="flex-1 min-w-0">
              <div class="flex items-start justify-between">
                <div class="flex-1 min-w-0 pr-2">
                  <p class="text-sm font-medium text-gray-900 truncate">
                    {{ notification.title }}
                  </p>
                  <div class="flex items-center space-x-1.5 mt-0.5">
                    <span
                      v-if="!notification.is_read"
                      class="h-1.5 w-1.5 bg-blue-500 rounded-full flex-shrink-0"
                    ></span>
                    <span class="text-[11px] font-medium text-gray-400">
                      {{ formatTime(notification.created_at) }}
                    </span>
                  </div>
                </div>
              </div>
              <p class="text-xs text-gray-600 mt-1 mb-2 line-clamp-2 leading-relaxed">
                {{ notification.message }}
              </p>

              <!-- Machine info for machine inquiries -->
              <div v-if="notification.data?.machine_title" class="text-[11px] font-medium text-blue-700 bg-blue-50/80 px-2 py-1.5 rounded truncate border border-blue-100/50 mt-1">
                <i class="fas fa-cog mr-1 opacity-60"></i>
                {{ notification.data.machine_title }}
              </div>
            </div>

            <!-- Actions -->
            <div class="flex-shrink-0 flex items-center space-x-1">
              <button
                @click="toggleNotificationRead(notification)"
                class="p-2 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                :title="notification.is_read ? 'Mark as unread' : 'Mark as read'"
              >
                <i
                  :class="notification.is_read ? 'fas fa-envelope-open' : 'fas fa-envelope'"
                  class="h-4 w-4"
                ></i>
              </button>
              <button
                @click="deleteNotification(notification.id)"
                class="p-2 rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                title="Delete notification"
              >
                <i class="fas fa-trash h-4 w-4"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div v-if="notifications.length > 0" class="px-4 py-3 border-t border-gray-100 bg-gray-50/50">
        <div class="flex items-center justify-between">
          <NuxtLink
            to="/admin/notifications"
            class="text-sm text-blue-600 hover:text-blue-800 font-medium flex items-center"
            @click="showNotifications = false"
          >
            <i class="fas fa-external-link-alt mr-2"></i>
            View all notifications
          </NuxtLink>
          <span class="text-xs text-gray-500">
            {{ notifications.length }} of {{ adminStore.notificationStats?.total || 0 }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { useSweetAlert } from '~/composables/useSweetAlert'

const adminStore = useAdminStore()
const { showSuccess, showError, showConfirm } = useSweetAlert()

const showNotifications = ref(false)
const loading = ref(false)
const notifications = computed(() => {
  if (!adminStore.notifications || !Array.isArray(adminStore.notifications)) {
    return []
  }
  return adminStore.notifications.slice(0, 10)
})

const toggleNotifications = async () => {
  showNotifications.value = !showNotifications.value

  if (showNotifications.value && (!adminStore.notifications || adminStore.notifications.length === 0)) {
    await loadNotifications()
  }
}

const loadNotifications = async () => {
  try {
    loading.value = true
    await adminStore.fetchNotifications(1, false)
  } catch (error) {
  } finally {
    loading.value = false
  }
}

const refreshNotifications = async () => {
  await loadNotifications()
  await adminStore.getNotificationStats()
}

const markAllAsRead = async () => {
  try {
    await adminStore.markAllNotificationsAsRead()
    showSuccess('All notifications marked as read')
  } catch (error) {
    showError('Failed to mark all notifications as read')
  }
}

const toggleNotificationRead = async (notification) => {
  try {
    if (notification.is_read) {
      await adminStore.markNotificationAsUnread(notification.id)
    } else {
      await adminStore.markNotificationAsRead(notification.id)
    }
  } catch (error) {
    showError('Failed to update notification')
  }
}

const deleteNotification = async (notificationId) => {
  try {
    const confirmed = await showConfirm(
      'Delete Notification',
      'Are you sure you want to delete this notification?',
      'Delete',
      'Cancel'
    )

    if (confirmed) {
      await adminStore.deleteNotification(notificationId)
      showSuccess('Notification deleted')
      await loadNotifications()
    }
  } catch (error) {
    showError('Failed to delete notification')
  }
}

const navigateToContact = (notification) => {
  showNotifications.value = false

  const contactData = notification.data

  if (contactData?.admin_path) {
    window.location.href = contactData.admin_path
    return
  }

  if (contactData && contactData.contact_id) {
    let contactType = 'general'

    if (contactData.route_type === 'machine' || contactData.source_model === 'ContactInquiry') {
      contactType = 'machine'
    } else if (contactData.route_type === 'sell' || contactData.inquiry_type === 'sell') {
      contactType = 'sell'
    }

    window.location.href = `/admin/contacts/${contactData.contact_id}/show?type=${encodeURIComponent(contactType)}`
  } else {
    window.location.href = '/admin/contacts'
  }
}

const getNotificationIcon = (type) => {
  switch (type) {
    case 'machine_inquiry':
      return 'fas fa-cog'
    case 'machine_quote_request':
      return 'fas fa-file-invoice-dollar'
    case 'spare_part_inquiry':
      return 'fas fa-tools'
    case 'spare_part_quote_request':
      return 'fas fa-file-signature'
    case 'sell_inquiry':
      return 'fas fa-hand-holding-usd'
    case 'newsletter_subscription':
      return 'fas fa-user-plus'
    case 'general_inquiry':
      return 'fas fa-envelope'
    default:
      return 'fas fa-bell'
  }
}

const getNotificationIconClass = (type) => {
  switch (type) {
    case 'machine_inquiry':
      return 'bg-blue-100 text-blue-600'
    case 'machine_quote_request':
      return 'bg-indigo-100 text-indigo-600'
    case 'spare_part_inquiry':
      return 'bg-amber-100 text-amber-700'
    case 'spare_part_quote_request':
      return 'bg-orange-100 text-orange-700'
    case 'sell_inquiry':
      return 'bg-emerald-100 text-emerald-700'
    case 'newsletter_subscription':
      return 'bg-purple-100 text-purple-700'
    case 'general_inquiry':
      return 'bg-green-100 text-green-600'
    default:
      return 'bg-gray-100 text-gray-600'
  }
}

const formatTime = (dateString) => {
  const date = new Date(dateString)
  const now = new Date()
  const diffInMinutes = Math.floor((now - date) / (1000 * 60))

  if (diffInMinutes < 1) return 'Just now'
  if (diffInMinutes < 60) return `${diffInMinutes}m ago`

  const diffInHours = Math.floor(diffInMinutes / 60)
  if (diffInHours < 24) return `${diffInHours}h ago`

  const diffInDays = Math.floor(diffInHours / 24)
  if (diffInDays < 7) return `${diffInDays}d ago`

  return date.toLocaleDateString()
}

onMounted(() => {
  if (process.client) {
    try {
      adminStore.getNotificationStats().catch(error => {
      })
    } catch (error) {
    }

    try {
      adminStore.startNotificationPolling()
    } catch (error) {
    }

    document.addEventListener('click', (event) => {
      if (!event.target.closest('.relative')) {
        showNotifications.value = false
      }
    })
  }
})
</script>

<style scoped>
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* Ensure dropdown is not constrained by parent containers */
.relative {
  position: relative;
}

/* Force dropdown to break out of any width constraints */
.absolute {
  position: absolute !important;
  width: max-content !important;
  min-width: 320px !important;
  max-width: 36rem !important;
}

/* Override global max-width constraint from width-fixes.css */
.notification-dropdown {
  max-width: none !important;
  width: max-content !important;
}

/* Override the aggressive width-fixes.css rule */
.notification-dropdown,
.notification-dropdown *,
.notification-dropdown *::before,
.notification-dropdown *::after {
  max-width: none !important;
}

/* Ensure proper z-index */
.z-50 {
  z-index: 9999 !important;
}

/* Force badge to be compact - override any global styles */
.notification-badge {
  width: 12px !important;
  height: 12px !important;
  min-width: 12px !important;
  max-width: 12px !important;
  padding: 0 !important;
  margin: 0 !important;
  font-size: 8px !important;
  line-height: 1 !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  border-radius: 50% !important;
  flex-shrink: 0 !important;
  box-sizing: border-box !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
  white-space: nowrap !important;
}

/* Override any global span or flex styles */
.notification-badge,
.notification-badge * {
  max-width: 12px !important;
  min-width: 12px !important;
  width: 12px !important;
}
</style>
